import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import BlobButton from './BlobButton';
import { ChevronLeft, ChevronRight, Calendar, Clock } from 'lucide-react';

const blogs = [
  {
    id: 1,
    slug: 'art-of-storytelling',
    title: 'The Art of Storytelling: Tips from Bestselling Authors',
    image: 'https://images.unsplash.com/photo-1455390582262-044cdead277a?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'Discover proven techniques to captivate readers and create compelling narratives.',
    author: 'Sarah Mitchell',
    date: 'Jan 15, 2025',
    readTime: '5 min read',
    category: 'Writing Tips'
  },
  {
    id: 2,
    slug: 'self-publishing-vs-traditional',
    title: 'Self-Publishing vs Traditional Publishing: Making the Right Choice',
    image: 'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'Understand the pros and cons of each publishing path to make an informed decision.',
    author: 'Michael Chen',
    date: 'Jan 12, 2025',
    readTime: '7 min read',
    category: 'Publishing'
  },
  {
    id: 3,
    slug: 'book-marketing-strategies-2025',
    title: 'Book Marketing Strategies That Actually Work in 2025',
    image: 'https://images.unsplash.com/photo-1432821596592-e2c18b78144f?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'Learn cutting-edge marketing techniques to boost your book sales and reach wider audiences.',
    author: 'Emily Rodriguez',
    date: 'Jan 10, 2025',
    readTime: '6 min read',
    category: 'Marketing'
  },
  {
    id: 4,
    title: 'Cover Design Trends: What Readers Love in 2025',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'Explore the latest trends in book cover design and how to create visually stunning covers.',
    author: 'James Anderson',
    date: 'Jan 8, 2025',
    readTime: '4 min read',
    category: 'Design'
  },
  {
    id: 5,
    title: 'Building Your Author Platform: A Complete Guide',
    image: 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'Step-by-step guide to establishing a strong online presence and connecting with readers.',
    author: 'Rebecca Turner',
    date: 'Jan 5, 2025',
    readTime: '8 min read',
    category: 'Author Growth'
  },
  {
    id: 6,
    title: 'The Future of Publishing: AI and Technology',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?crop=entropy&cs=srgb&fm=jpg&q=85&w=600',
    excerpt: 'How artificial intelligence and new technologies are reshaping the publishing industry.',
    author: 'David Park',
    date: 'Jan 3, 2025',
    readTime: '6 min read',
    category: 'Technology'
  }
];

const newsItems = [
  {
    id: 1,
    slug: 'new-distribution-partnership',
    title: 'New Book Distribution Partnership Announced',
    date: 'Jan 18, 2025',
    category: 'Company News'
  },
  {
    id: 2,
    slug: 'award-winning-author-joins',
    title: 'Award-Winning Author Joins Our Team',
    date: 'Jan 16, 2025',
    category: 'Team Update'
  },
  {
    id: 3,
    slug: 'expanded-global-distribution',
    title: 'Expanded Global Distribution Network',
    date: 'Jan 14, 2025',
    category: 'Expansion'
  }
];

const BlogsAndNews = () => {
  const [currentBlogIndex, setCurrentBlogIndex] = useState(0);
  const blogsPerPage = 2;
  const totalPages = Math.ceil(blogs.length / blogsPerPage);

  const nextBlogs = () => {
    setCurrentBlogIndex((prev) => (prev + blogsPerPage) % blogs.length);
  };

  const prevBlogs = () => {
    setCurrentBlogIndex((prev) => (prev - blogsPerPage + blogs.length) % blogs.length);
  };

  const currentBlogs = [
    blogs[currentBlogIndex],
    blogs[(currentBlogIndex + 1) % blogs.length]
  ];

  return (
    <section className="py-20 bg-transparent">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-700 rounded-full mb-6">
            <span className="text-amber-700 text-sm font-semibold tracking-wider">INSIGHTS & UPDATES</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">
            Blogs & <span className="text-amber-700">News</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay updated with the latest publishing insights, tips, and company news.
          </p>
        </div>

        {/* Single Row Layout: Blogs Slider (70%) + News (30%) */}
        <div className="flex flex-col lg:flex-row gap-8 items-stretch">
          {/* Blogs Slider Section - 70% width */}
          <div className="lg:w-[70%]">
            <div className="relative">
              {/* Two Blog Cards Side by Side */}
              <div className="grid md:grid-cols-2 gap-6">
                {currentBlogs.map((blog) => (
                  <Card key={blog.id} className="overflow-hidden shadow-2xl rounded-2xl flex flex-col h-full">
                    {/* Blog Image */}
                    <div className="relative h-48 overflow-hidden flex-shrink-0">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-amber-700 text-white text-xs font-semibold rounded-full">
                          {blog.category}
                        </span>
                      </div>
                    </div>

                    {/* Blog Content */}
                    <div className="p-6 bg-white flex flex-col flex-grow">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2">{blog.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2 flex-grow">{blog.excerpt}</p>
                      
                      <div className="space-y-3 mt-auto">
                        <div className="flex flex-col gap-2 text-xs text-gray-500">
                          <span className="font-medium text-amber-700">{blog.author}</span>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              <span>{blog.date}</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              <span>{blog.readTime}</span>
                            </div>
                          </div>
                        </div>

                        <Link to={`/blog/${blog.slug}`} className="w-full">
                          <BlobButton className="w-full text-sm">
                            Read Article
                          </BlobButton>
                        </Link>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Navigation Arrows */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <Button
                  onClick={prevBlogs}
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 rounded-full border-2 border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>

                {/* Page Indicators */}
                <div className="flex gap-2">
                  {Array.from({ length: totalPages }).map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentBlogIndex(index * blogsPerPage)}
                      className={`h-2.5 rounded-full transition-all ${
                        Math.floor(currentBlogIndex / blogsPerPage) === index
                          ? 'bg-amber-700 w-6'
                          : 'bg-gray-300 w-2.5 hover:bg-gray-400'
                      }`}
                      aria-label={`Go to page ${index + 1}`}
                    />
                  ))}
                </div>

                <Button
                  onClick={nextBlogs}
                  variant="outline"
                  size="icon"
                  className="w-10 h-10 rounded-full border-2 border-amber-700 text-amber-700 hover:bg-amber-700 hover:text-white transition-all"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>

          {/* News Section - 30% width, matching height */}
          <div className="lg:w-[30%] flex">
            <Card className="p-6 shadow-2xl rounded-2xl bg-white flex flex-col w-full">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <span className="w-2 h-2 bg-amber-700 rounded-full"></span>
                Latest News
              </h3>
              
              <div className="space-y-5 flex-grow">
                {newsItems.map((news) => (
                  <Link
                    key={news.id}
                    to={`/news/${news.slug}`}
                    className="block pb-5 border-b border-gray-200 last:border-0 cursor-pointer hover:bg-gray-50 p-3 rounded-lg transition-all"
                  >
                    <span className="text-xs text-amber-700 font-semibold">{news.category}</span>
                    <h4 className="text-base font-bold text-gray-900 mt-2 mb-2 leading-snug hover:text-amber-700 transition-colors">
                      {news.title}
                    </h4>
                    <div className="flex items-center gap-2 text-xs text-gray-500">
                      <Calendar className="w-3 h-3" />
                      <span>{news.date}</span>
                    </div>
                  </Link>
                ))}
              </div>

              <Link to="/news-updates">
                <BlobButton className="w-full mt-6">
                  View All News & Updates
                </BlobButton>
              </Link>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BlogsAndNews;